import { OTS_DAYS_OF_WEEK, OtsDayOfWeek } from './OtsDayOfWeekPicker';

/**
 * Daily/weekly time windows, matching the server's `ScheduleWindow` (restrictions, route-person
 * schedules, …). Times are milliseconds after local midnight; days are `DayOfWeek` names.
 */
export type OtsScheduleType = 'DAILY' | 'WEEKLY';

export type OtsScheduleWindow = {
  type: string;
  startTime: number;
  endTime: number;
  daysOfWeek?: string[];
};

/** A window being edited; `key` keeps rows stable before the server assigns ids. */
export type OtsScheduleWindowDraft = {
  key: string;
  type: OtsScheduleType;
  start: string;
  end: string;
  days: string[];
};

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS: OtsDayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
const pad = (value: number) => String(value).padStart(2, '0');

/** Milliseconds after midnight → `HH:mm`. */
export const scheduleMsToTime = (ms?: number) => {
  const minutes = Math.floor(((((ms ?? 0) % DAY_MS) + DAY_MS) % DAY_MS) / 60000);
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
};

/** `HH:mm` → milliseconds after midnight. */
export const scheduleTimeToMs = (time: string) => {
  const [hour, minute] = time.split(':').map(Number);
  return ((hour || 0) * 60 + (minute || 0)) * 60000;
};

export const sortScheduleDays = (days?: string[]) =>
  [...(days ?? [])].sort(
    (left, right) =>
      OTS_DAYS_OF_WEEK.indexOf(left as OtsDayOfWeek) - OTS_DAYS_OF_WEEK.indexOf(right as OtsDayOfWeek)
  );

const dayShortLabel = (day: string) => day.charAt(0) + day.slice(1, 3).toLowerCase();

/** "Weekdays", "Weekends", "Every day", or "Mon, Wed". */
export const formatScheduleDays = (days?: string[]) => {
  const sorted = sortScheduleDays(days);
  if (sorted.length === 7) return 'Every day';
  if (sorted.join() === WEEKDAYS.join()) return 'Weekdays';
  if (sorted.join() === 'SATURDAY,SUNDAY') return 'Weekends';
  return sorted.map(dayShortLabel).join(', ');
};

/** "Weekdays 07:00–17:00" or "Daily 20:00–06:00 (overnight)". */
export const formatScheduleWindow = (window: OtsScheduleWindow) => {
  const start = scheduleMsToTime(window.startTime);
  const end = scheduleMsToTime(window.endTime);
  const overnight = scheduleTimeToMs(end) <= scheduleTimeToMs(start);
  const days = window.type === 'WEEKLY' ? formatScheduleDays(window.daysOfWeek) : 'Daily';
  return `${days} ${start}–${end}${overnight ? ' (overnight)' : ''}`;
};

let draftKey = 0;
export const newScheduleWindowDraft = (
  value: Partial<Omit<OtsScheduleWindowDraft, 'key'>> = {}
): OtsScheduleWindowDraft => ({
  key: `schedule-window-${++draftKey}`,
  type: value.type ?? 'WEEKLY',
  start: value.start ?? '07:00',
  end: value.end ?? '17:00',
  days: value.days ?? [...WEEKDAYS],
});

export const toScheduleWindowDrafts = (windows?: OtsScheduleWindow[]): OtsScheduleWindowDraft[] =>
  (windows ?? []).map((window) =>
    newScheduleWindowDraft({
      type: window.type === 'DAILY' ? 'DAILY' : 'WEEKLY',
      start: scheduleMsToTime(window.startTime),
      end: scheduleMsToTime(window.endTime),
      days: sortScheduleDays(window.daysOfWeek),
    })
  );

export const fromScheduleWindowDrafts = (drafts: OtsScheduleWindowDraft[]): OtsScheduleWindow[] =>
  drafts.map((draft) => ({
    type: draft.type,
    startTime: scheduleTimeToMs(draft.start),
    endTime: scheduleTimeToMs(draft.end),
    daysOfWeek: draft.type === 'WEEKLY' ? sortScheduleDays(draft.days) : undefined,
  }));

/** The first problem with a set of windows, or undefined when they are all valid. */
export const validateScheduleWindows = (drafts: OtsScheduleWindowDraft[]) => {
  const invalid = drafts.find(
    (draft) => draft.start === draft.end || (draft.type === 'WEEKLY' && draft.days.length === 0)
  );
  if (!invalid) return undefined;
  return invalid.start === invalid.end
    ? 'Each window needs different start and end times.'
    : 'Weekly windows need at least one day.';
};
